import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import { authStore } from '../../../api/stores/globalStores';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * DevLanding Component
 * Hero/landing section for the Developers page.
 * Desktop: absolute-positioned 1024px canvas (hidden lg:block)
 * Mobile: vertically stacked flow layout (flex lg:hidden)
 * Transparent background — inherits from page background zones.
 */

const WHITE = 'rgba(255, 255, 255, 1)';
const YELLOW = 'rgba(251, 191, 36, 1)';

const NEBULA_LARGE =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?w=1360&e=webp&cX=0&cY=0&cW=1438&cH=831';
const NEBULA_SMALL =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?w=1326&e=webp&cX=0&cY=5&cW=1440&cH=825';
const CARD_BG =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-d217b4f0-1f13-40e7-b72b-43599c667da7.png?w=1517&e=webp&nll=true&cX=52&cY=0&cW=1864&cH=1080';
const DEVICE_LEFT = '/img/aida/HomeThreePlatformOptions/Option1/threeplatformphone1.png';
const DEVICE_RIGHT = '/img/aida/HomeThreePlatformOptions/Option2/threeplatformlaptop2.png';

const ACCORDION_ITEMS = [
  {
    title: 'POWER OF DROP-IN ADS',
    body: 'Using our host network, players can instantly drop into games — bringing PC-only game demos to any device.',
  },
  {
    title: 'BRING PLAYERS ANYWHERE',
    body: 'Playcast makes it easy to connect your preferred game store to players. We drop viewers to your preferred store of choice to purchase or wishlist.',
  },
];

const Accordion = ({ compact = false }: { compact?: boolean }) => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div style={{ width: '100%', boxSizing: 'border-box', padding: '0 4px' }}>
      {ACCORDION_ITEMS.map((item, i) => (
        <div key={i} style={{ position: 'relative' }}>
          {i > 0 && (
            <div style={{ height: '1px', background: 'rgba(255,255,255,0.5)', margin: '0 20px' }} />
          )}
          <div
            role="button"
            tabIndex={0}
            aria-expanded={openIndex === i}
            onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: `Accordion: ${item.title}`, buttonLocation: 'DevLanding' }); setOpenIndex(openIndex === i ? (i === 0 ? 1 : 0) : i); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpenIndex(openIndex === i ? (i === 0 ? 1 : 0) : i); }}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 20px',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: compact ? '13.5px' : '16px', letterSpacing: '0.02em', color: WHITE, textTransform: 'uppercase' }}>
              {item.title}
            </span>
            <span style={{
              position: 'relative',
              display: 'inline-block',
              width: '20px',
              height: '20px',
              flexShrink: 0,
              cursor: 'pointer',
              transform: openIndex === i ? 'rotate(45deg)' : 'none',
              transition: 'transform 0.35s ease',
            }}>
              <span style={{ position: 'absolute', left: '3px', top: '9px', width: '14px', height: '2px', background: WHITE }} />
              <span style={{ position: 'absolute', left: '9px', top: '3px', width: '2px', height: '14px', background: WHITE }} />
            </span>
          </div>
          <div style={{
            overflow: 'hidden',
            maxHeight: openIndex === i ? (compact ? '80px' : '160px') : '0px',
            transition: 'max-height 0.35s ease, padding 0.35s ease',
            padding: openIndex === i ? '0 20px 12px' : '0 20px',
            fontFamily: 'Inter, sans-serif',
            fontSize: compact ? '11px' : '16px',
            lineHeight: compact ? 1.35 : 1.5,
            fontWeight: 350,
            color: '#ccc',
          }}>
            {item.body}
          </div>
        </div>
      ))}
    </div>
  );
};

export const DevLanding = () => {
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(desktopRef, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });
  const navigate = useNavigate();

  return (
    <>
      {/* ── Desktop Layout ── */}
      <motion.section
        ref={desktopRef}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          height: '530px',
          backgroundColor: 'transparent',
          overflow: 'visible',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* Nebula — top-left */}
          <div style={{ position: 'absolute', left: '-331px', top: '-332px', width: '605px', height: '350px', zIndex: 382, transform: 'rotate(-99deg) scaleX(-1)', pointerEvents: 'none' }}>
            <img src={NEBULA_LARGE} className="fixMobileImageStretch" alt="" style={{ opacity: 0.43 }} draggable={false} />
          </div>

          {/* Nebula — top-right */}
          <div style={{ position: 'absolute', left: '713px', top: '-209px', width: '605px', height: '350px', zIndex: 380, transform: 'rotate(-99deg) scaleX(-1)', pointerEvents: 'none' }}>
            <img src={NEBULA_LARGE} className="fixMobileImageStretch" alt="" style={{ opacity: 0.43 }} draggable={false} />
          </div>

          {/* Nebula — left */}
          <div style={{ position: 'absolute', left: '-279px', top: '123px', width: '590px', height: '338px', zIndex: 383, transform: 'rotate(-99deg)', pointerEvents: 'none' }}>
            <img src={NEBULA_SMALL} className="fixMobileImageStretch" alt="" style={{ opacity: 0.27 }} draggable={false} />
          </div>

          {/* Nebula — right */}
          <div style={{ position: 'absolute', left: '765px', top: '246px', width: '590px', height: '338px', zIndex: 381, transform: 'rotate(-99deg)', pointerEvents: 'none' }}>
            <img src={NEBULA_SMALL} className="fixMobileImageStretch" alt="" style={{ opacity: 0.27 }} draggable={false} />
          </div>

          {/* Background card */}
          <div style={{
            position: 'absolute',
            left: '175px',
            top: '83px',
            width: '675px',
            height: '391px',
            zIndex: 402,
            borderRadius: '31px',
            backgroundImage: `url("${CARD_BG}")`,
            backgroundPosition: '50% 50%',
            backgroundSize: 'cover',
            opacity: 0.59,
          }} />

          {/* Title */}
          <div style={{ position: 'absolute', left: '247px', top: '143px', width: '336px', zIndex: 403 }}>
            <p style={{ lineHeight: '39px', fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: '32px', fontStyle: 'italic', letterSpacing: '-0.5px', color: WHITE, margin: 0, fontVariationSettings: "'wght' 900" }}>
              GAME DISCOVERY
            </p>
            <p style={{ lineHeight: '39px', fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: '32px', fontStyle: 'italic', letterSpacing: '-0.5px', margin: 0, fontVariationSettings: "'wght' 900" }}>
              <span style={{ color: WHITE }}>MADE </span>
              <span style={{ color: YELLOW }}>SIMPLE.</span>
            </p>
          </div>

          {/* Accordion */}
          <div style={{ position: 'absolute', left: '227px', top: '233px', width: '286px', zIndex: 407 }}>
            <Accordion compact />
          </div>

          {/* Device image — right (laptop, platform 2) */}
          <div style={{ position: 'absolute', left: '550px', top: '122px', width: '276px', height: '282px', zIndex: 405 }}>
            <img src={DEVICE_RIGHT} className="fixMobileImageStretch" alt="" style={{ opacity: 1, width: '100%', height: '100%', objectFit: 'contain' }} draggable={false} />
          </div>

          {/* Device image — left (phone, platform 1) */}
          <div style={{ position: 'absolute', left: '463px', top: '174px', width: '246px', height: '196px', zIndex: 406, filter: 'drop-shadow(rgba(3, 10, 42, 0.23) 8px 6px 2px)' }}>
            <img src={DEVICE_LEFT} className="fixMobileImageStretch" alt="" style={{ opacity: 1, width: '100%', height: '100%', objectFit: 'contain' }} draggable={false} />
          </div>

          {/* Button — Partner With Us */}
          <button
            className="aida-button"
            onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Partner With Us', buttonLocation: 'DevLanding' }); navigate('/devs/onboarding'); }}
            style={{
              position: 'absolute',
              left: '247px',
              top: '404px',
              width: '144px',
              height: '30px',
              borderRadius: '30px',
              zIndex: 416,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '30px',
              cursor: 'pointer',
            }}
          >
            <span className="text" style={{ height: '30px', lineHeight: '30px' }}>Partner With Us</span>
          </button>

          {/* Button — Learn More */}
          <button
            className="aida-button aida-button-outline-fill"
            onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Learn More', buttonLocation: 'DevLanding' }); const el = document.getElementById('dev-explore'); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY, behavior: 'smooth' }); }}
            style={{
              position: 'absolute',
              left: '404px',
              top: '404px',
              width: '144px',
              height: '30px',
              borderRadius: '30px',
              zIndex: 417,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '30px',
              cursor: 'pointer',
            }}
          >
            <span className="text" style={{ height: '30px', lineHeight: '30px' }}>Learn More</span>
          </button>
        </motion.div>
      </motion.section>

      {/* ── Mobile Layout ── */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          backgroundColor: 'transparent',
          padding: '80px 16px 40px',
          gap: '20px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div style={{ width: '100%', maxWidth: '370px', borderRadius: '20px', overflow: 'hidden', backgroundImage: `url("${CARD_BG}")`, backgroundPosition: '50% 50%', backgroundSize: 'cover', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p style={{ lineHeight: '40px', fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: '34px', fontStyle: 'italic', letterSpacing: '-0.5px', color: WHITE, margin: 0, fontVariationSettings: "'wght' 900", textAlign: 'center' }}>
            GAME DISCOVERY<br />
            <span style={{ color: WHITE }}>MADE </span>
            <span style={{ color: YELLOW }}>SIMPLE.</span>
          </p>
          <Accordion />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
            <button className="aida-button" onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Partner With Us', buttonLocation: 'DevLanding' }); navigate('/devs/onboarding'); }} style={{ width: '100%', height: '35px', borderRadius: '44px', fontSize: '18px', fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', lineHeight: '35px' }}>
              <span className="text" style={{ height: '35px', lineHeight: '35px' }}>Partner With Us</span>
            </button>
            <button className="aida-button aida-button-outline-fill" onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Learn More', buttonLocation: 'DevLanding' }); const el = document.getElementById('dev-explore'); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY, behavior: 'smooth' }); }} style={{ width: '100%', height: '35px', borderRadius: '44px', fontSize: '18px', fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', lineHeight: '35px' }}>
              <span className="text" style={{ height: '35px', lineHeight: '35px' }}>Learn More</span>
            </button>
          </div>
        </div>
      </motion.section>
    </>
  );
};
